import { useEffect } from "react";
import { site } from "@/config/site";

type Props = {
  title: string;
  description: string;
  path?: string;
  jsonLd?: object | object[];
};

function setMeta(selector: string, attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function SEO({ title, description, path = "", jsonLd }: Props) {
  useEffect(() => {
    document.title = title;
    setMeta('meta[name="description"]', "name", "description", description);
    setMeta('meta[property="og:title"]', "property", "og:title", title);
    setMeta('meta[property="og:description"]', "property", "og:description", description);
    setMeta('meta[property="og:type"]', "property", "og:type", "website");
    setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", description);

    if (site.domain) {
      const href = `https://${site.domain}${path}`;
      let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", href);
      setMeta('meta[property="og:url"]', "property", "og:url", href);
    }

    // JSON-LD
    document.querySelectorAll('script[data-seo-jsonld]').forEach((n) => n.remove());
    const blocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
    const orgLd = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: site.siteName,
      email: site.brandEmail,
      url: site.domain ? `https://${site.domain}` : undefined,
    };
    const siteLd = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: site.siteName,
      url: site.domain ? `https://${site.domain}` : undefined,
    };
    [orgLd, siteLd, ...blocks].forEach((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.dataset.seoJsonld = "true";
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
    });
  }, [title, description, path, JSON.stringify(jsonLd)]);

  return null;
}

export default SEO;
